import React, { useState, useCallback } from 'react';
import { Text, RadioButton, TwoLineDivider, Button, Stack } from '@moda/om';
import { throwQueryError } from '../../../../lib/apollo/throwQueryError';
import { useValidateAddressQuery, RestAddressAttributes } from '../../../../hooks/useAddress';
import { ModalOverlay } from '../../../Modal';
import { ShippingAddressPreview } from '../../ShippingAddressPreview';
import './ShippingAddressValidationPopup.scss';

enum AddressOption {
  AddressEntered,
  VerifiedMatch
}

interface Props {
  attributes: RestAddressAttributes;
  onSubmit: (attributes: RestAddressAttributes) => void;
  onClose: () => void;
}

export const ShippingAddressValidationPopup: React.FC<Props> = ({
  attributes,
  onSubmit,
  onClose
}) => {
  const [selectedOption, setSelectedOption] = useState<AddressOption | undefined>(undefined);
  const { address: verifiedMatch, loading, error } = useValidateAddressQuery(attributes);

  const handleSubmit = useCallback(() => {
    switch (selectedOption) {
      case AddressOption.AddressEntered: {
        onSubmit(attributes);
        break;
      }
      case AddressOption.VerifiedMatch: {
        if (verifiedMatch) {
          onSubmit(verifiedMatch.attributes);
        }
        break;
      }
      default:
        return;
    }
  }, [attributes, onSubmit, selectedOption, verifiedMatch]);

  if (error) throwQueryError(error);

  if (loading) return null;

  return (
    <ModalOverlay className="ShippingAddressValidationPopup" onClose={onClose}>
      <Stack className="ShippingAddressValidationPopup__content" space={6}>
        <Text family="serif" treatment="h4">
          Please confirm this postal address
        </Text>

        <TwoLineDivider text="Address entered" />

        <RadioButton
          checked={selectedOption === AddressOption.AddressEntered}
          onChange={() => setSelectedOption(AddressOption.AddressEntered)}
        >
          <ShippingAddressPreview attributes={attributes} />
        </RadioButton>

        {verifiedMatch && (
          <>
            <TwoLineDivider text="Verified Match" />

            <RadioButton
              checked={selectedOption === AddressOption.VerifiedMatch}
              onChange={() => setSelectedOption(AddressOption.VerifiedMatch)}
            >
              <ShippingAddressPreview attributes={verifiedMatch.attributes} />
            </RadioButton>
          </>
        )}

        <Button disabled={selectedOption === undefined} onClick={handleSubmit} tabIndex={-1}>
          Confirm Address
        </Button>
      </Stack>
    </ModalOverlay>
  );
};
